import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NavStateService {
  private readonly CACHE_KEY = 'navbar_cache';
  private readonly CACHE_TIMESTAMP_KEY = 'navbar_cache_timestamp';
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutos en milisegundos
  private readonly CACHE_REFRESH_THRESHOLD = 8 * 60 * 1000; // Refrescar después de 8 minutos
  
  private navbarSubject = new BehaviorSubject<any[]>([]);
  public navbar$ = this.navbarSubject.asObservable();
  
  private isExpandedSubject = new BehaviorSubject<boolean>(false);
  public isExpanded$ = this.isExpandedSubject.asObservable();
  
  private expandedItemsSubject = new BehaviorSubject<Set<string>>(new Set());
  public expandedItems$ = this.expandedItemsSubject.asObservable();
  
  // Subject para notificar cuando se debe recargar el menú
  private reloadTrigger = new Subject<void>();
  public reload$ = this.reloadTrigger.asObservable();
  
  constructor() {
    // Intentar cargar el menú desde caché al inicializar el servicio
    this.loadFromCache();
  }
  
  setNavbar(navbar: any[]) {
    this.navbarSubject.next(navbar);
    // Guardar en caché
    this.saveToCache(navbar);
  }
  
  private saveToCache(navbar: any[]) {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(navbar));
      localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (e) {
      console.warn('No se pudo guardar el menú en caché:', e);
    }
  }
  
  private loadFromCache() {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      const timestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
      
      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10);
        if (age < this.CACHE_DURATION) {
          const navbar = JSON.parse(cached);
          this.navbarSubject.next(navbar);
        } else {
          // Caché expirado, limpiar
          this.clearCache();
        }
      }
    } catch (e) {
      console.warn('No se pudo cargar el menú desde caché:', e);
    }
  }
  
  private clearCache() {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
    } catch (e) {
      console.warn('No se pudo limpiar el caché:', e);
    }
  }
  
  isCacheExpiringSoon(): boolean {
    try {
      const timestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
      if (!timestamp) {
        return true;
      }
      const age = Date.now() - parseInt(timestamp, 10);
      return age > this.CACHE_REFRESH_THRESHOLD;
    } catch (e) {
      return true;
    }
  }
  
  getCacheAge(): number {
    try {
      const timestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
      if (!timestamp) {
        return Infinity;
      }
      return Date.now() - parseInt(timestamp, 10);
    } catch (e) {
      return Infinity;
    }
  }
  
  setExpanded(expanded: boolean) {
    this.isExpandedSubject.next(expanded);
  }
  
  toggleExpanded() {
    this.isExpandedSubject.next(!this.isExpandedSubject.value);
  }
  
  setItemExpanded(itemId: string, expanded: boolean) {
    const currentSet = new Set(this.expandedItemsSubject.value);
    if (expanded) {
      currentSet.add(itemId);
    } else {
      currentSet.delete(itemId);
    }
    this.expandedItemsSubject.next(currentSet);
  }
  
  isItemExpanded(itemId: string): boolean {
    return this.expandedItemsSubject.value.has(itemId);
  }
  
  get currentNavbar() {
    return this.navbarSubject.value;
  }
  
  get currentExpanded() {
    return this.isExpandedSubject.value;
  }
  
  // Método para actualizar el estado de expansión de un item específico
  updateNavbarItemExpansion(navbar: any[]) {
    const updatedNavbar = navbar.map(item => ({
      ...item,
      Expanded: this.isItemExpanded(item.Title || item.Route || item.id)
    }));
    this.navbarSubject.next(updatedNavbar);
    return updatedNavbar;
  }

  // Método para limpiar completamente el estado del navbar
  clearNavbar() {
    this.navbarSubject.next([]);
    this.expandedItemsSubject.next(new Set());
    this.isExpandedSubject.next(false);
    this.clearCache();
  }

  // Método para forzar la recarga del menú desde cualquier componente
  forceReload() {
    this.clearNavbar();
    this.reloadTrigger.next();
  }
}